import { type FC, forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Item, MenuItem, MenuOptionItem, MenuSection, MenuTriggerButton, TriggerButtonSize } from "widgets";

import { getEventTagIntent, getTagIntentColor } from "@/app-utils/events.js";
import {
  getIconElementForPlaybook,
  getPlaybookEventResourcesSummary,
  isPlaybookExecutableInPlaybookPageContext,
} from "@/app-utils/playbook.js";
import { pluralizeWord } from "@/app-utils/string.js";
import type { Dictionary } from "@/app-utils/type.js";
import type { Configuration, EventResource, PlaybookEvent } from "@/graphql.js";
import style from "@/modules/core/styles/Tile.module.scss";
import { isPlaybookLicensed } from "@/redux/modules/config.js";
import { openPlaybook } from "@/redux/modules/playbook-execution.js";
import type { IAppState } from "@/redux/reducer.js";
import type { ReduxDispatch } from "@/redux/redux-utils.js";

import { Tile } from "../../../../modules/core/components/Tile.js";

interface PlaybookEventTileProps {
  event: PlaybookEvent;
}

const EventInfo: FC<PlaybookEventTileProps> = (props) => {
  const { alias, note, shortSummary, results } = props.event;
  const resourceSummary: Dictionary<EventResource[]> = getPlaybookEventResourcesSummary(results);
  const eventResults: string[] = Object.entries(resourceSummary).map(([categoryName, resources]) => {
    const resourceCount = resources.length;
    return `${resourceCount} ${pluralizeWord(categoryName, resourceCount)}`;
  });

  const eventInfoExists = alias || note || eventResults?.length || shortSummary;
  return (
    eventInfoExists && (
      <div className={style.event_info}>
        {(alias || note) && (
          <div>
            {alias && <div className={style.event_info_title}>{alias}</div>}
            {note && <div className={style.event_info_desc}>{note}</div>}
          </div>
        )}
        <div className={style.event_data_wrapper}>
          {shortSummary && <div className={style.event_data}>{shortSummary}</div>}
          {eventResults?.map((res) => (
            <div className={style.event_data} key={res}>
              {res}
            </div>
          ))}
        </div>
      </div>
    )
  );
};

const PlaybookEventTile = forwardRef<HTMLDivElement, PlaybookEventTileProps>(function PlaybookEventTile(props, ref) {
  const dispatch = useDispatch<ReduxDispatch>();
  const { event } = props;
  const { playbook, project } = event;
  const statusIntent = getEventTagIntent(event.status);
  const playbookIcon = playbook && getIconElementForPlaybook(playbook);
  const configuration = useSelector<IAppState, Configuration>((state) => state.config.configuration);
  const licensedPlaybook = isPlaybookLicensed(configuration, playbook);
  const isExecutableinPlaybookPageContext = isPlaybookExecutableInPlaybookPageContext(playbook);

  const playbookName = event.job ? `${event.job.tool.toUpperCase()} Search` : playbook?.name;
  return (
    <Tile
      dates={{ created: event.created }}
      users={{ creator: event.owner }}
      title={playbookName}
      project={project}
      subtitle={playbook.nameSuffix}
      to={`/event/${event.id}`}
      rightIcon={<img width={75} src={playbookIcon?.large} />}
      // We don't show explicitly a colored label when an Event is completed. This is the default awaited behavior of the application
      // do not show colored label at all, see SEL9-820
      // coloredLabel={event.status !== EVENT_STATUS.COMPLETED ? [{ color: getEventTagIntentColor(statusIntent), label: event.status }] : undefined}
      eventStatusBulletColor={getTagIntentColor(statusIntent) ?? null}
      footNote={<EventInfo event={event} />}
      menuButton={
        licensedPlaybook && isExecutableinPlaybookPageContext ? (
          <MenuTriggerButton
            icon={"more_vert"}
            size={TriggerButtonSize.Small}
            label={""}
            onAction={(key) => {
              switch (String(key)) {
                case "launch":
                  dispatch(openPlaybook(playbook));
                  break;
              }
            }}
          >
            <Item key={"launch"}>
              <MenuOptionItem
                title={`Launch ${playbook.name}`}
                icon={<img src={playbookIcon?.small} width={18} height={18} />}
              />
            </Item>
          </MenuTriggerButton>
        ) : undefined
      }
      ref={ref}
    />
  );
});

export { PlaybookEventTile };
